import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import webSocketService from "../api/websocket-service";
import { getNotifications } from "../api/api";

const NotificationsContext = createContext();

export const useNotifications = () => useContext(NotificationsContext);

const TECHNICAL_TYPES = ['init_audio', 'end_audio'];

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [newNotifications, setNewNotifications] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastKey, setLastKey] = useState(null);
  const technicalListeners = useRef([]);
  const initialized = useRef(false);
  
  const fetchNotifications = async (reset = false) => {
    try {
      setIsLoading(true);
      const params = { limit: 20 };
      
      if (!reset && lastKey) {
        params.lastKey = lastKey;
      }
      
      const response = await getNotifications(params);
      
      const userNotifications = response.items || [];
      
      let sortedNotifications;
      if (reset) {
        sortedNotifications = [...userNotifications].sort((a, b) => b.timestamp - a.timestamp);
      } else {
        sortedNotifications = [...notifications, ...userNotifications].sort((a, b) => b.timestamp - a.timestamp);
      }
      
      setNotifications(sortedNotifications);
      setHasMore(response.hasMore);
      setLastKey(response.lastKey);
    } catch (error) {
      console.error("Error al cargar notificaciones:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadMoreNotifications = () => {
    if (hasMore && !isLoading) {
      fetchNotifications(false);
    }
  };
  
  const handleNewNotification = useCallback((notification) => {
    if (!notification) return;
    
    // Si es una notificación técnica
    if (TECHNICAL_TYPES.includes(notification.type)) {
      technicalListeners.current.forEach(listener => {
        try {
          listener(notification);
        } catch (err) {
          console.error("Error en listener técnico:", err);
        }
      });
    } 
    // Si es una notificación normal
    else {
      setNotifications(prev => {
        const updatedNotifications = [notification, ...prev];
        return updatedNotifications.sort((a, b) => 
          (b.timestamp || 0) - (a.timestamp || 0)
        );
      });
      
      setNewNotifications(true);
    }
  }, []);

  const markNotificationsAsRead = () => {
    console.log("Marcando notificaciones como leídas");
    setNewNotifications(false);
  };
  
  const addTechnicalListener = useCallback((callback) => {
    if (typeof callback !== 'function') {
      return () => {};
    }
    technicalListeners.current.push(callback);
    return () => {
      technicalListeners.current = technicalListeners.current.filter(
        listener => listener !== callback
      );
    };
  }, []);
  
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    
    fetchNotifications(true);
    
    webSocketService.connect();
    const unsubscribe = webSocketService.addListener(handleNewNotification);
    
    return () => {
      unsubscribe();
    };
  }, [handleNewNotification]);

  return (
    <NotificationsContext.Provider value={{
      notifications,
      fetchNotifications,
      hasNewNotifications: newNotifications,
      markNotificationsAsRead,
      addTechnicalListener,
      loadMoreNotifications,
      hasMore,
      isLoading
    }}>
      {children}
    </NotificationsContext.Provider>
  );
};